import { ApiConfig } from "../apiConfig/apiConfig";
import { apiDelete, apiGet, apiPost, apiPostForm, apiPut, apiPutForm } from "../apiConfig/apiCall";

const AdminService = {
  /**
   * Get admin activity logs
   * @param {Object} params - { page, limit, admin_id, module, action }
   */
  getAdminLogs: async (params = {}) => {
    const { baseAdmin, adminLogs } = ApiConfig;
    const url = baseAdmin + adminLogs;
    return apiGet(url, {}, params);
  },

  /**
   * Get dashboard counts (all values in USDT)
   */
  getDashboardCounts: async () => {
    const { baseAdmin, dashboardCounts } = ApiConfig;
    const url = baseAdmin + dashboardCounts;
    return apiGet(url);
  },

  // ========= Enquiries (Contact Form) =========
  /** Get all contact form submissions (newest first) */
  getContactForms: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.contactFormList),

  /** Update a submission's status. status: "Read" | "Resolved" */
  setContactFormStatus: (id, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.contactFormSetStatus, { _id: id, status }),

  /**
   * Get user list (traders)
   * @param {Object} params - { page, limit, search, kyc_status, status }
   */
  getUsers: async (params = {}) => {
    const { baseAdmin, adminUsers } = ApiConfig;
    const url = baseAdmin + adminUsers;
    return apiGet(url, {}, params);
  },

  getUserDetail: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userDetail(userId)),
  getUserKyc: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userKyc(userId)),
  /**
   * Get KYC list (all users)
   * @param {Object} params - { status, page, limit, search }
   * status: pending | approved | rejected | all
   */
  getKycList: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.kycList, {}, params),
  updateUserStatus: (userId, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.userStatus, { userId, status }),
  approveKyc: (userId, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycApprove, { userId, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  rejectKyc: (userId, rejectReason, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycReject, { userId, reject_reason: rejectReason, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  approveKycDocument: (userId, documentType, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycApproveDocument, { userId, document_type: documentType, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  rejectKycDocument: (userId, documentType, rejectReason, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycRejectDocument, { userId, document_type: documentType, reject_reason: rejectReason, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  getUserWallets: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userWallets(userId), {}, params),
  getUserTransactions: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userTransactions(userId), {}, params),
  getUserInternalTransfers: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userInternalTransfers(userId), {}, params),
  getUserSpotTrades: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSpotTrades(userId), {}, params),
  getUserSpotOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSpotOrders(userId), {}, params),
  getUserSwapHistory: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSwapHistory(userId), {}, params),
  getUserEarningPlans: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userEarningPlans(userId), {}, params),
  getUserLaunchpad: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userLaunchpad(userId), {}, params),
  getUserSupportTickets: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSupportTickets(userId), {}, params),
  getUserReferral: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userReferral(userId)),
  getUserP2pOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pOrders(userId), {}, params),
  getUserP2pAds: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pAds(userId), {}, params),
  getUserP2pPaymentMethods: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pPaymentMethods(userId), {}, params),
  getUserFuturesTrades: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesTrades(userId), {}, params),
  getUserFuturesOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesOrders(userId), {}, params),
  getUserFuturesPositions: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesPositions(userId), {}, params),

  /** Get coins config (available coins + supported chains with regex) */
  getCoinsConfig: () => apiGet(ApiConfig.baseAdmin + ApiConfig.coinsConfig),

  /** Get lightweight coins list (_id, name, short_name) */
  getCoinsList: () => apiGet(ApiConfig.baseAdmin + ApiConfig.coinsList),

  /** Create new coin (multipart/form-data) */
  createCoin: (formData) => apiPostForm(ApiConfig.baseAdmin + ApiConfig.coinsCreate, formData),

  /** Update coin (multipart/form-data) */
  updateCoin: (coinId, formData) => apiPutForm(ApiConfig.baseAdmin + ApiConfig.coinsUpdate(coinId), formData),

  /** Get pair info for prefill (Binance/CMC/LOCAL) */
  getPairInfo: (body) => apiPost(ApiConfig.baseAdmin + ApiConfig.pairInfo, body),

  /** Create trading pair */
  createPair: (body) => apiPost(ApiConfig.baseAdmin + ApiConfig.pairsCreate, body),

  /** Get pairs list. Params: { status?: "Active" | "Inactive" } */
  getPairsList: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.pairsList, {}, params),

  /** List / Delist pair (update status: "Active" | "Inactive") */
  updatePairStatus: (pairId, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.pairStatus(pairId), { status }),

  /** Get platform settings (features: enabled, scheduled_at, scheduled_action) */
  getPlatformConfig: () => apiGet(ApiConfig.baseAdmin + ApiConfig.platformConfig),

  /** Update platform settings (instant: { key: bool } or scheduled: { key: { enabled, scheduled_at, scheduled_action } }) */
  updatePlatformConfig: (body) => apiPut(ApiConfig.baseAdmin + ApiConfig.platformConfig, body),

  /** Cancel scheduled change for a feature */
  cancelPlatformSchedule: (feature) =>
    apiDelete(ApiConfig.baseAdmin + ApiConfig.platformCancelSchedule(feature)),

  // ========= Launchpad =========
  /** Get currencies for launchpad (base & quote selection) */
  getLaunchpadCurrencies: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadCurrencies),

  /** Create launchpad (application/json) */
  createLaunchpad: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.launchpadCreate, body),

  /** List launchpads with pagination. Params: { page, limit, status } */
  getLaunchpadListing: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadListing, {}, params),

  /** Get launchpad by ID */
  getLaunchpadDetail: (id) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadDetail(id)),

  /** Update launchpad (application/json) */
  updateLaunchpad: (id, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.launchpadUpdate(id), body),

  /** Update launchpad status. Body: { id, status } */
  updateLaunchpadStatus: (id, status) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.launchpadUpdateStatus, { id, status }),

  /** Get launchpad sales tracking. Params: { page, limit } */
  getLaunchpadSalesTracking: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadSalesTracking, {}, params),

  /** Get all launchpads summary (aggregate) */
  getLaunchpadSummary: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadSummary),
};

export default AdminService;
